import { openDB, type DBSchema } from 'idb';
import type { LocalText, OfflinePackId } from '../../domain/tools/types';

export interface OfflinePackAsset {
  readonly url: string;
  readonly bytes: number;
  readonly sha256: string;
}

export interface OfflinePackDefinition {
  readonly id: OfflinePackId;
  readonly version: string;
  readonly name: LocalText;
  readonly description: LocalText;
  readonly downloadSize: number;
  readonly installedSize: number;
  readonly features: Readonly<Record<'en' | 'ar', readonly string[]>>;
  readonly requirements: LocalText;
  readonly kind: 'ocr' | 'model' | 'fallback';
  readonly available: boolean;
  readonly assets: readonly OfflinePackAsset[];
}

export interface OfflinePackManifest {
  readonly schemaVersion: 1;
  readonly generatedAt: string;
  readonly packs: readonly OfflinePackDefinition[];
}

export interface InstalledPack {
  readonly id: OfflinePackId;
  readonly version: string;
  readonly installedAt: number;
  readonly verifiedBytes: number;
}

interface PackDatabase extends DBSchema {
  installed: {
    key: OfflinePackId;
    value: InstalledPack;
  };
}

export interface PackProgress {
  readonly packId: OfflinePackId;
  readonly loaded: number;
  readonly total: number;
  readonly currentAsset: string;
}

export class PackInstallError extends Error {
  public readonly code: 'network' | 'size' | 'integrity' | 'unavailable' | 'manifest';

  public constructor(code: PackInstallError['code'], message: string) {
    super(message);
    this.name = 'PackInstallError';
    this.code = code;
  }
}

const MANIFEST_URL = '/offline-packs/manifest.json';
const PACK_CACHE = 'satr-offline-packs-v1';
const DB_NAME = 'satr-offline-pack-state';
const TESSERACT_CACHE_DB_NAME = 'keyval-store';
const stateEvent = 'satr-pack-state';

let manifestPromise: Promise<OfflinePackManifest> | undefined;

function deleteDatabase(name: string): Promise<void> {
  return new Promise((resolve) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onerror = () => resolve();
    request.onblocked = () => resolve();
  });
}

function database() {
  return openDB<PackDatabase>(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('installed')) db.createObjectStore('installed', { keyPath: 'id' });
    },
  });
}

function assertSameOriginPath(path: string): URL {
  const url = new URL(path, window.location.origin);
  if (url.origin !== window.location.origin || !url.pathname.startsWith('/offline-packs/')) {
    throw new PackInstallError('manifest', `Rejected non-pack asset path: ${path}`);
  }
  return url;
}

function isManifest(value: unknown): value is OfflinePackManifest {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<OfflinePackManifest>;
  return candidate.schemaVersion === 1 && Array.isArray(candidate.packs) && candidate.packs.every((pack) => (
    typeof pack.id === 'string'
    && typeof pack.version === 'string'
    && typeof pack.downloadSize === 'number'
    && Array.isArray(pack.assets)
  ));
}

export async function loadPackManifest(force = false): Promise<OfflinePackManifest> {
  if (!manifestPromise || force) {
    manifestPromise = fetch(MANIFEST_URL, { cache: 'no-cache', credentials: 'same-origin' })
      .then(async (response) => {
        if (!response.ok) throw new PackInstallError('network', `Manifest returned ${response.status}`);
        const value: unknown = await response.json();
        if (!isManifest(value)) throw new PackInstallError('manifest', 'Offline pack manifest is invalid.');
        for (const pack of value.packs) for (const asset of pack.assets) assertSameOriginPath(asset.url);
        return value;
      });
  }
  return manifestPromise;
}

export async function listInstalledPacks(): Promise<readonly InstalledPack[]> {
  const db = await database();
  return db.getAll('installed');
}

export async function isPackInstalled(id: OfflinePackId, version?: string): Promise<boolean> {
  const db = await database();
  const installed = await db.get('installed', id);
  return Boolean(installed && (!version || installed.version === version));
}

async function sha256(data: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', Uint8Array.from(data).buffer);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function fetchAsset(
  asset: OfflinePackAsset,
  loadedBefore: number,
  total: number,
  packId: OfflinePackId,
  onProgress?: (progress: PackProgress) => void,
  signal?: AbortSignal,
): Promise<Response> {
  const url = assertSameOriginPath(asset.url);
  let response: Response;
  try {
    response = await fetch(url, { cache: 'no-store', credentials: 'same-origin', signal });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new PackInstallError('network', `Could not download ${asset.url}`);
  }
  if (!response.ok) throw new PackInstallError('network', `${asset.url} returned ${response.status}`);

  const contentType = response.headers.get('Content-Type') ?? 'application/octet-stream';
  const chunks: Uint8Array[] = [];
  let assetLoaded = 0;

  if (response.body) {
    const reader = response.body.getReader();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        chunks.push(value);
        assetLoaded += value.byteLength;
        onProgress?.({ packId, loaded: loadedBefore + assetLoaded, total, currentAsset: asset.url });
      }
    }
  } else {
    const bytes = new Uint8Array(await response.arrayBuffer());
    chunks.push(bytes);
    assetLoaded = bytes.byteLength;
  }

  if (assetLoaded !== asset.bytes) {
    throw new PackInstallError('size', `${asset.url} expected ${asset.bytes} bytes but received ${assetLoaded}`);
  }

  const bytes = new Uint8Array(assetLoaded);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }

  if (await sha256(bytes) !== asset.sha256.toLowerCase()) {
    throw new PackInstallError('integrity', `Integrity check failed for ${asset.url}`);
  }

  onProgress?.({ packId, loaded: loadedBefore + assetLoaded, total, currentAsset: asset.url });
  return new Response(bytes, {
    status: 200,
    headers: {
      'Content-Type': contentType,
      'Content-Length': String(assetLoaded),
      'X-Satr-Pack-SHA256': asset.sha256,
    },
  });
}

export async function installPack(
  id: OfflinePackId,
  onProgress?: (progress: PackProgress) => void,
  signal?: AbortSignal,
): Promise<InstalledPack> {
  const manifest = await loadPackManifest();
  const pack = manifest.packs.find((candidate) => candidate.id === id);
  if (!pack) throw new PackInstallError('manifest', `Unknown pack: ${id}`);
  if (!pack.available) throw new PackInstallError('unavailable', `Pack is not available: ${id}`);

  const temporaryCacheName = `${PACK_CACHE}-install-${id}-${pack.version}`;
  await caches.delete(temporaryCacheName);
  const temporary = await caches.open(temporaryCacheName);
  let loaded = 0;

  try {
    for (const asset of pack.assets) {
      if (signal?.aborted) throw new DOMException('Installation canceled', 'AbortError');
      const response = await fetchAsset(asset, loaded, pack.downloadSize, id, onProgress, signal);
      await temporary.put(assertSameOriginPath(asset.url), response);
      loaded += asset.bytes;
    }

    const permanent = await caches.open(PACK_CACHE);
    for (const asset of pack.assets) {
      const request = assertSameOriginPath(asset.url);
      const response = await temporary.match(request);
      if (!response) throw new PackInstallError('integrity', `Temporary cache lost ${asset.url}`);
      await permanent.put(request, response);
    }

    const installed: InstalledPack = { id, version: pack.version, installedAt: Date.now(), verifiedBytes: loaded };
    const db = await database();
    await db.put('installed', installed);
    window.dispatchEvent(new CustomEvent(stateEvent, { detail: id }));
    return installed;
  } finally {
    await caches.delete(temporaryCacheName);
  }
}

export async function removePack(id: OfflinePackId): Promise<void> {
  const manifest = await loadPackManifest();
  const pack = manifest.packs.find((candidate) => candidate.id === id);
  const db = await database();
  const installed = await db.getAll('installed');
  const retainedPackIds = new Set(installed.filter((entry) => entry.id !== id).map((entry) => entry.id));
  const retainedAssetUrls = new Set(manifest.packs
    .filter((candidate) => retainedPackIds.has(candidate.id))
    .flatMap((candidate) => candidate.assets.map((asset) => asset.url)));
  const cache = await caches.open(PACK_CACHE);
  if (pack) {
    await Promise.all(pack.assets
      .filter((asset) => !retainedAssetUrls.has(asset.url))
      .map((asset) => cache.delete(assertSameOriginPath(asset.url))));
  }
  if (pack?.kind === 'ocr') await deleteDatabase(TESSERACT_CACHE_DB_NAME);
  await db.delete('installed', id);
  window.dispatchEvent(new CustomEvent(stateEvent, { detail: id }));
}

export async function installAllPacks(
  onProgress?: (progress: PackProgress) => void,
  signal?: AbortSignal,
): Promise<readonly InstalledPack[]> {
  const manifest = await loadPackManifest();
  const installed: InstalledPack[] = [];
  for (const pack of manifest.packs.filter((candidate) => candidate.available)) {
    if (signal?.aborted) throw new DOMException('Installation canceled', 'AbortError');
    installed.push(await installPack(pack.id, onProgress, signal));
  }
  return installed;
}

export async function clearPackCache(): Promise<void> {
  const keys = await caches.keys();
  await Promise.all([
    ...keys.filter((key) => key.startsWith(PACK_CACHE)).map((key) => caches.delete(key)),
    deleteDatabase(TESSERACT_CACHE_DB_NAME),
  ]);
  const db = await database();
  await db.clear('installed');
  window.dispatchEvent(new CustomEvent(stateEvent));
}

export function onPackStateChange(listener: () => void): () => void {
  window.addEventListener(stateEvent, listener);
  return () => window.removeEventListener(stateEvent, listener);
}

export async function clearTemporaryTaskData(): Promise<void> {
  await deleteDatabase('satr-task-store');

  try {
    const storageWithDirectory = navigator.storage as StorageManager & { getDirectory?: () => Promise<FileSystemDirectoryHandle> };
    const root = await storageWithDirectory.getDirectory?.();
    if (root) await root.removeEntry('satr-tasks', { recursive: true });
  } catch {
    // OPFS is optional and can be unavailable or already clear.
  }
}
